import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Dialog, useIsDark } from '@highlight/ui'
import HighlightLogoIcon from '@renderer/components/Icons/HighlightLogoIcon'
import {
  planContinueClasses,
  planLogoStyle,
  planTierLabel,
  planWashDotsStyle,
  planWashStyle,
  type SelectableV2Plan,
} from './plan-select-cards'

/**
 * The tier-tinted chassis of Figma's "Subscription Dialog": wash + dot grid
 * behind a centered title and message, with the footer content below. Shape
 * comes from the caller: the meeting-hours paywall renders it as a floating
 * card, the sidepanel plan banner as a full-width strip, and
 * SubscriptionDialogModal as a centered modal.
 */
export function SubscriptionDialog({
  plan,
  title,
  message,
  brandMark = false,
  className,
  children,
}: {
  plan: SelectableV2Plan
  title: string
  message: string
  /** Heads the wash with a tier-gradient logo tile. The modal shows it; the docked strips don't. */
  brandMark?: boolean
  className?: string
  children: ReactNode
}) {
  const isDark = useIsDark()

  return (
    <div className={twMerge('bg-background relative overflow-hidden [font-family:Geist,Inter,sans-serif]', className)}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-24">
        <div className="absolute inset-0" style={planWashStyle(plan, isDark)} />
        <div className="absolute inset-0" style={planWashDotsStyle(plan, isDark)} />
      </div>

      {brandMark && (
        <div className="relative flex justify-center px-4 pt-8">
          <div
            aria-hidden="true"
            className="shadow-background/50 flex size-16 items-center justify-center rounded-2xl border shadow-[0px_4px_16px_0px]"
            style={planLogoStyle(plan)}
          >
            <HighlightLogoIcon size={36} color="black" />
          </div>
        </div>
      )}

      <div className="relative flex flex-col gap-1 px-4 pb-1.5 pt-4 text-center">
        <h3 className="text-foreground m-0 text-[18px] font-medium leading-normal">{title}</h3>
        <p className="text-foreground m-0 text-[15px] leading-normal">{message}</p>
      </div>

      <div className="relative flex flex-col gap-4 p-4">{children}</div>
    </div>
  )
}

/**
 * The subscription dialog as a centered modal, for surfaces that open a
 * top-up on demand (the Subscription settings tab) rather than docking one
 * because billing is already blocked. The paywall body is the child, so it
 * stays identical to the docked presentations.
 */
export function SubscriptionDialogModal({
  open,
  onOpenChange,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-[524px] overflow-hidden rounded-2xl shadow-md">
          {/* A purchase dialog needs a visible way out, not just Escape and the
              backdrop. Solid rather than the usual translucent chip: this one
              sits on the tier wash, where a see-through fill leaves the glyph
              barely darker than the color behind it. */}
          <Dialog.Close
            aria-label="Close"
            variant="outline"
            shape="circle"
            size="xs"
            className="absolute right-3 top-3 z-10 flex"
          >
            <X className="size-3" strokeWidth={3} />
          </Dialog.Close>
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog>
  )
}

/**
 * The "OR / Upgrade to <tier>" alternative below a top-up CTA. Rendered only
 * when a higher tier exists, so Max never offers one.
 */
export function UpgradeAlternativeCta({
  targetTier,
  isPending,
  disabled,
  onUpgrade,
}: {
  targetTier: SelectableV2Plan
  isPending?: boolean
  disabled?: boolean
  onUpgrade: (tier: SelectableV2Plan) => void
}) {
  return (
    <>
      <div aria-hidden="true" className="flex items-center gap-2.5">
        <div className="bg-element-active h-px flex-1" />
        <span className="text-foreground-disabled text-[12px] font-medium">OR</span>
        <div className="bg-element-active h-px flex-1" />
      </div>
      <button
        type="button"
        onClick={() => onUpgrade(targetTier)}
        disabled={disabled || isPending}
        className="bg-background text-foreground-secondary hover:bg-element h-[42px] rounded-xl text-[16px] font-medium shadow-sm transition-colors disabled:opacity-60"
      >
        {isPending ? 'Processing…' : `Upgrade to ${planTierLabel(targetTier)}`}
      </button>
    </>
  )
}

/** The dialog's primary purchase CTA with its card-on-file disclaimer, in the tier's accent. */
export function PurchaseCta({
  plan,
  label,
  isPending,
  disabled,
  onClick,
}: {
  plan: SelectableV2Plan
  label: string
  isPending?: boolean
  /** Disables without the Processing label, e.g. while a sibling CTA's charge is in flight. */
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isPending}
      className={twMerge(
        'h-[42px] rounded-xl text-[16px] font-medium shadow-sm transition-colors disabled:opacity-60',
        planContinueClasses(plan),
      )}
    >
      {isPending ? 'Processing…' : label}
    </button>
  )
}

/**
 * {@link PurchaseCta} under the charge notice, for the surfaces that only sell
 * to a user who already has a card. A free account has none, so its CTA opens
 * Stripe checkout and must not promise a charge.
 */
export function CardOnFileCta(props: {
  plan: SelectableV2Plan
  label: string
  isPending?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <div className="bg-element-hover flex flex-col rounded-xl">
      <div className="text-foreground-secondary flex h-8 items-center justify-center text-[13px]">
        Your card on file will be charged.
      </div>
      <PurchaseCta {...props} />
    </div>
  )
}
